/**
 * Submit Results Tool Schema
 *
 * Schema for the submitResults tool that all agents-as-tools use to return
 * their results to the parent agent. This pattern allows the LLM to:
 * 1. Loop through multiple tool calls before deciding to submit
 * 2. Accumulate and deduplicate results across calls
 * 3. Provide structured results with rationales
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════════════════════
// RESULT ITEM SCHEMA
// ═══════════════════════════════════════════════════════════════════════════
/**
 * A single result item that maps to one requirement.
 * The relevantResults field is flexible to accommodate different agent types.
 */
export const ResultItemSchema = z.object({
    /** ID of the requirement this result addresses (e.g., "A", "B", "1") */
    requirementId: z.string().describe("The requirement ID this result addresses"),
    /**
     * The actual result data - structure depends on the agent type.
     * Could be search results, assets, pages, etc.
     */
    relevantResults: z.record(z.string(), z.unknown()).describe("Agent-specific result data (searchResults, assets, pages, etc.)"),
    /** Explanation of why/how this result addresses the requirement */
    rationale: z.string().describe("Explanation of why these results satisfy the requirement"),
});
// ═══════════════════════════════════════════════════════════════════════════
// SUBMIT RESULTS PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Parameters for the submitResults tool.
 * Called by agents to return their final results to the parent agent.
 */
export const SubmitResultsParamsSchema = z.object({
    /** Array of results, one per requirement */
    results: z.array(ResultItemSchema).describe("Array of result items, one per requirement addressed"),
    /** Brief summary of what was accomplished */
    summary: z.string().optional().describe("Optional brief summary of findings"),
    /** Any warnings or notes for the parent agent */
    notes: z.string().optional().describe("Optional notes or warnings for the parent agent"),
});
